import { askAboutAndReveal } from "../../../state/chatActions";
import type {
  EthocaAlert,
  EvidencePackage,
  MerchantSummary,
  Tc40Record,
} from "../../../data/types";
import { fmtEur } from "./format";
import styles from "./DisputeTimeline.module.css";

interface DisputeTimelineProps {
  merchant: MerchantSummary;
  tc40: Tc40Record[];
  ethoca: EthocaAlert[];
  evidence: EvidencePackage[];
  limit?: number;
}

type Event =
  | {
      at: string;
      kind: "tc40";
      text: string;
      amount: number;
      suppressed: boolean;
      prompt: string;
    }
  | { at: string; kind: "ethoca"; text: string; amount: number; prompt: string }
  | { at: string; kind: "evidence"; text: string; amount: number; prompt: string };

export function DisputeTimeline({
  merchant,
  tc40,
  ethoca,
  evidence,
  limit = 12,
}: DisputeTimelineProps) {
  const evs: Event[] = [];
  tc40
    .filter((r) => r.merchantId === merchant.id)
    .forEach((r) =>
      evs.push({
        at: r.date,
        kind: "tc40",
        text: `tc40 · reason ${r.reasonCode}${r.matchedCE3 ? " · ce3 matched" : ""}`,
        amount: r.amountEur,
        suppressed: r.suppressed,
        prompt: `walk me through ${r.id} on ${merchant.name} — reason ${r.reasonCode}, ${fmtEur(r.amountEur)}, ${r.matchedCE3 ? (r.suppressed ? "ce3 matched + suppressed" : "ce3 matched but not suppressed") : "no ce3 match"}. what was the pathway and what would a future one with a stronger signal look like?`,
      })
    );
  ethoca
    .filter((a) => a.merchantId === merchant.id)
    .forEach((a) =>
      evs.push({
        at: a.receivedAt,
        kind: "ethoca",
        text: `ethoca alert · ${a.status}${a.action ? ` · ${a.action}` : ""}`,
        amount: a.amountEur,
        prompt: `ethoca alert ${a.id} on ${merchant.name}: ${fmtEur(a.amountEur)}, status ${a.status}${a.action ? ` via ${a.action}` : ""}. was this the right call? what would the chargeback cost have been otherwise?`,
      })
    );
  evidence
    .filter((e) => e.merchantId === merchant.id)
    .forEach((e) =>
      evs.push({
        at: e.assembledAt ?? e.disputeDate,
        kind: "evidence",
        text: `evidence · ${e.status}`,
        amount: e.amountEur,
        prompt: `evidence package ${e.id} on ${merchant.name} — reason ${e.reasonCode}, ${fmtEur(e.amountEur)}, status ${e.status}. what evidence went in, and if we lost, what was missing?`,
      })
    );

  const sorted = evs.sort((a, b) => b.at.localeCompare(a.at)).slice(0, limit);

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>dispute timeline</span>
        <span className={styles.muted}>{merchant.name}</span>
      </header>
      <ul className={styles.list}>
        {sorted.map((e, i) => {
          const tone =
            e.kind === "tc40"
              ? e.suppressed
                ? styles.goodBorder
                : styles.warnBorder
              : e.kind === "ethoca"
                ? styles.infoBorder
                : styles.goodBorder;
          return (
            <li key={i}>
              <button
                type="button"
                className={`${styles.item} ${tone}`}
                onClick={() => askAboutAndReveal(e.prompt)}
                title="drill into this event"
              >
                <span className={styles.date}>{e.at}</span>
                <span className={styles.text}>{e.text}</span>
                <span className={styles.amount}>{fmtEur(e.amount)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
